"use client";

import { useState, useEffect } from 'react';

type AdminUser = {
  id: number;
  email: string;
  company_id: number;
  company_name: string | null;
  tokens_balance: number;
};

type UserForm = {
  email: string;
  password: string;
  company_name: string;
  tokens_balance: string;
};

const INPUT_STYLE = {
  width: '100%', padding: '0.65rem 0.9rem', borderRadius: '8px',
  border: '1px solid #374151', background: '#111827',
  color: '#f3f4f6', fontSize: '0.9rem', boxSizing: 'border-box' as const,
};

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<'settings' | 'users'>('settings');

  // ── Users state ────────────────────────────────────────────────────────────
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Edit modal
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState<UserForm>({ email: '', password: '', company_name: '', tokens_balance: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Add user form (inline, above table)
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<UserForm>({ email: '', password: '', company_name: '', tokens_balance: '100' });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  // Delete confirm
  const [deleteUserId, setDeleteUserId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Settings state ─────────────────────────────────────────────────────────
  const [grayZone, setGrayZone] = useState({ enabled: true, width: 20 });
  const [analysisSettings, setAnalysisSettings] = useState({
    enableExif: true, enableC2pa: true, enableAiDetector: true, aiSensitivity: 50,
  });
  const [llmSettings, setLlmSettings] = useState({
    enabled: true,
    url: 'http://192.168.0.14:1234/v1/chat/completions',
    model: 'gemma-4-e2b-it',
    key: 'lm-studio',
    rules: {
      exifMissing: 'Izguba sledi: Odsotnost EXIF pomeni, da nimaš neposrednih dokazov o času nastanka, napravi (znamka/model telefona ali fotoaparata) ali geografski lokaciji (GPS).',
      exifPresent: 'Sled prisotna: Slika vsebuje EXIF podatke. Preveri programska orodja v metapodatkih in oceni potencialno urejanje.',
      c2paValid: 'Kriptografsko potrjeno: Slika vsebuje veljaven C2PA certifikat. Zgodovina te slike je zanesljiva in digitalno podpisana.',
      c2paInvalid: 'Opozorilo o izvoru: Slika nima C2PA certifikata ali pa ta ni veljaven. Posveti se ostalim dokazom (EXIF, AI).',
      aiManipulated: 'AI Manipulacija: Detektor nevronske mreže (ViT) je z visoko gotovostjo zaznal sintetične piksle (umetno inteligenco).',
      aiAuthentic: 'Naravni piksli: Nevronska mreža (ViT) ni zaznala nobenih znakov AI generiranja ali forenzičnih manipulacij.',
    },
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getToken = () => typeof window !== 'undefined' ? localStorage.getItem('ff_token') : null;
  const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

  // ── Load persisted settings ────────────────────────────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem('fotoforensics_admin_settings');
    if (saved) {
      try {
        const p = JSON.parse(saved);
        if (p.grayZone) setGrayZone(p.grayZone);
        if (p.analysisSettings) setAnalysisSettings(p.analysisSettings);
        if (p.llmSettings) setLlmSettings(prev => ({ ...prev, ...p.llmSettings, rules: { ...prev.rules, ...p.llmSettings.rules } }));
      } catch { /* ignore */ }
    }
  }, []);

  // ── Fetch users ────────────────────────────────────────────────────────────
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await fetch('/api/admin/users', { headers: authHeader() });
      if (res.ok) setUsers(await res.json());
    } finally {
      setLoadingUsers(false);
    }
  };
  useEffect(() => { fetchUsers(); }, []);

  // ── Settings handlers ──────────────────────────────────────────────────────
  const handleSaveSettings = () => {
    setSavingSettings(true);
    setTimeout(() => {
      localStorage.setItem('fotoforensics_admin_settings', JSON.stringify({ grayZone, analysisSettings, llmSettings }));
      setSavingSettings(false);
      alert('Sistemske nastavitve so uspešno shranjene!');
    }, 800);
  };

  // ── User handlers ──────────────────────────────────────────────────────────
  const handleAddTokens = async (user: AdminUser) => {
    const res = await fetch(`/api/admin/users/${user.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ tokens_balance: user.tokens_balance + 100 }),
    });
    if (res.ok) {
      const updated = await res.json();
      setUsers(users.map(u => u.id === user.id ? { ...u, tokens_balance: updated.tokens_balance } : u));
    }
  };

  const handleOpenEdit = (user: AdminUser) => {
    setEditUser(user);
    setEditForm({ email: user.email, password: '', company_name: user.company_name || '', tokens_balance: String(user.tokens_balance) });
    setEditError('');
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    setEditSaving(true);
    setEditError('');
    try {
      const body: Record<string, string | number> = {
        email: editForm.email,
        company_name: editForm.company_name,
        tokens_balance: parseInt(editForm.tokens_balance) || 0,
      };
      if (editForm.password.trim()) body.password = editForm.password;
      const res = await fetch(`/api/admin/users/${editUser.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setEditError(data.detail || 'Napaka pri shranjevanju'); return; }
      setUsers(users.map(u => u.id === editUser.id ? { ...u, ...data } : u));
      setEditUser(null);
    } catch { setEditError('Napaka pri povezavi s strežnikom'); }
    finally { setEditSaving(false); }
  };

  const handleAddUser = async () => {
    setAddSaving(true);
    setAddError('');
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: addForm.email,
          password: addForm.password,
          company_name: addForm.company_name || 'Novo podjetje',
        }),
      });
      const data = await res.json();
      if (!res.ok) { setAddError(data.detail || 'Napaka pri dodajanju'); return; }
      // Optionally set tokens if different from default 100
      await fetchUsers();
      setAddForm({ email: '', password: '', company_name: '', tokens_balance: '100' });
      setShowAddForm(false);
    } catch { setAddError('Napaka pri povezavi s strežnikom'); }
    finally { setAddSaving(false); }
  };

  const handleDeleteUser = async () => {
    if (deleteUserId === null) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/users/${deleteUserId}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (res.ok) {
        setUsers(users.filter(u => u.id !== deleteUserId));
        setDeleteUserId(null);
      }
    } finally { setDeleting(false); }
  };

  // ── Tab button style helper ────────────────────────────────────────────────
  const tabStyle = (active: boolean) => ({
    padding: '0.6rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600,
    fontSize: '0.9rem', border: 'none', transition: 'all 0.15s',
    background: active ? 'rgba(99,102,241,0.2)' : 'transparent',
    color: active ? '#a5b4fc' : '#6b7280',
    borderBottom: active ? '2px solid #6366f1' : '2px solid transparent',
  });

  return (
    <main className="container animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ margin: 0 }}>Skrbniška plošča</h2>
        <div className="token-badge" style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#818cf8', borderColor: 'rgba(99, 102, 241, 0.2)' }}>
          <span>👑</span> Super Admin
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '2rem', borderBottom: '1px solid #1f2937', paddingBottom: '0' }}>
        <button style={tabStyle(activeTab === 'settings')} onClick={() => setActiveTab('settings')}>
          ⚙️ Nastavitve
        </button>
        <button style={tabStyle(activeTab === 'users')} onClick={() => setActiveTab('users')}>
          👥 Uporabniki {!loadingUsers && <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', background: 'rgba(99,102,241,0.15)', padding: '0.1rem 0.4rem', borderRadius: '10px' }}>{users.length}</span>}
        </button>
      </div>

      {/* ── NASTAVITVE TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'settings' && (
        <>
          {/* Moduli Analize */}
          <div className="glass-card delay-2" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1.5rem' }}>Moduli Analize</h3>
            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1.5rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
              {[
                { key: 'enableExif', label: 'EXIF Metapodatki', desc: 'Preveri skrite podatke o kameri, GPS lokaciji in programih za urejanje.' },
                { key: 'enableC2pa', label: 'C2PA Verifikacija (Content Credentials)', desc: 'Preveri prisotnost in veljavnost kriptografskega podpisa o izvoru datoteke.' },
                { key: 'enableAiDetector', label: 'AI Detektor (ViT Model)', desc: 'Uporabi model strojnega učenja za detekcijo umetne inteligence, če C2PA ni prisoten.' },
              ].map(({ key, label, desc }) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer', marginBottom: '1rem' }}>
                  <input type="checkbox"
                    checked={analysisSettings[key as keyof typeof analysisSettings] as boolean}
                    onChange={e => setAnalysisSettings({ ...analysisSettings, [key]: e.target.checked })}
                    style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ color: '#e5e7eb', fontWeight: 500 }}>{label}</div>
                    <div style={{ color: '#a0a0a0', fontSize: '0.85rem' }}>{desc}</div>
                  </div>
                </label>
              ))}
              {analysisSettings.enableAiDetector && (
                <div style={{ background: 'rgba(0,0,0,0.3)', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#e5e7eb', fontSize: '0.9rem', fontWeight: 500 }}>Občutljivost AI Detektorja</span>
                    <span style={{ color: '#818cf8', fontWeight: 600 }}>{analysisSettings.aiSensitivity}%</span>
                  </label>
                  <input type="range" min="10" max="90" step="5" value={analysisSettings.aiSensitivity}
                    onChange={e => setAnalysisSettings({ ...analysisSettings, aiSensitivity: parseInt(e.target.value) })}
                    style={{ width: '100%', cursor: 'pointer', accentColor: '#6366f1' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', color: '#6b7280', fontSize: '0.75rem' }}>
                    <span>Manj občutljivo</span><span>Bolj strogo</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* AI Gray Zone */}
          <div className="glass-card delay-2" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1.5rem' }}>Sistemske nastavitve AI Detektorja</h3>
            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1.5rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer', marginBottom: grayZone.enabled ? '1.5rem' : '0' }}>
                <input type="checkbox" checked={grayZone.enabled}
                  onChange={e => setGrayZone({ ...grayZone, enabled: e.target.checked })}
                  style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer' }}
                />
                <div>
                  <div style={{ color: '#e5e7eb', fontWeight: 500 }}>Omogoči sivo območje (Nejasen rezultat)</div>
                  <div style={{ color: '#a0a0a0', fontSize: '0.85rem' }}>Če je izklopljeno, bo veljala le stroga meja in AI detektor ne bo nikoli vrnil rezultata &quot;Nejasno&quot;.</div>
                </div>
              </label>
              {grayZone.enabled && (
                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#e5e7eb', fontSize: '0.9rem', fontWeight: 500 }}>Širina sivega območja (+/- %)</span>
                    <span style={{ color: '#818cf8', fontWeight: 600 }}>±{grayZone.width}%</span>
                  </label>
                  <input type="range" min="5" max="30" step="5" value={grayZone.width}
                    onChange={e => setGrayZone({ ...grayZone, width: parseInt(e.target.value) })}
                    style={{ width: '100%', cursor: 'pointer', accentColor: '#6366f1' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', color: '#6b7280', fontSize: '0.75rem' }}>
                    <span>Ožje</span><span>Širše</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* LLM */}
          <div className="glass-card delay-2" style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1.5rem' }}>LLM Asistent (Zunanji model)</h3>
            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1.5rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer', marginBottom: llmSettings.enabled ? '1.5rem' : '0' }}>
                <input type="checkbox" checked={llmSettings.enabled}
                  onChange={e => setLlmSettings({ ...llmSettings, enabled: e.target.checked })}
                  style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer' }}
                />
                <div>
                  <div style={{ color: '#e5e7eb', fontWeight: 500 }}>Omogoči generiranje LLM poročil</div>
                  <div style={{ color: '#a0a0a0', fontSize: '0.85rem' }}>Zunanji jezikovni model (npr. LM Studio) za strokovno razlago izsledkov.</div>
                </div>
              </label>
              {llmSettings.enabled && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#e5e7eb', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: 500 }}>API URL (OpenAI kompatibilen)</label>
                    <input type="text" value={llmSettings.url}
                      onChange={e => setLlmSettings({ ...llmSettings, url: e.target.value })}
                      style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '0.9rem' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', color: '#e5e7eb', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: 500 }}>Ime Modela</label>
                      <input type="text" value={llmSettings.model}
                        onChange={e => setLlmSettings({ ...llmSettings, model: e.target.value })}
                        style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '0.9rem' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', color: '#e5e7eb', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: 500 }}>API Ključ</label>
                      <input type="password" value={llmSettings.key}
                        onChange={e => setLlmSettings({ ...llmSettings, key: e.target.value })}
                        placeholder="Za LM Studio: 'lm-studio'"
                        style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '0.9rem' }}
                      />
                    </div>
                  </div>
                  <div>
                    <h4 style={{ color: '#818cf8', marginBottom: '1rem' }}>Forenzična Pravila (Prompt Engineering)</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {[
                        { key: 'exifMissing', label: 'Manjkajoč EXIF' },
                        { key: 'exifPresent', label: 'Prisoten EXIF' },
                        { key: 'c2paValid', label: 'Potrjen C2PA Certifikat' },
                        { key: 'c2paInvalid', label: 'Manjkajoč/Neveljaven C2PA' },
                        { key: 'aiManipulated', label: 'AI zaznal Manipulacijo' },
                        { key: 'aiAuthentic', label: 'AI potrdil Pristnost' },
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <label style={{ display: 'block', color: '#e5e7eb', fontSize: '0.85rem', marginBottom: '0.3rem' }}>{label}</label>
                          <textarea
                            value={llmSettings.rules[key as keyof typeof llmSettings.rules]}
                            onChange={e => setLlmSettings({ ...llmSettings, rules: { ...llmSettings.rules, [key]: e.target.value } })}
                            style={{ width: '100%', padding: '0.6rem', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '0.85rem', minHeight: '60px' }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <button className="btn-primary"
            style={{ marginBottom: '3rem', padding: '1rem 2rem', width: '100%', fontSize: '1.1rem' }}
            onClick={handleSaveSettings} disabled={savingSettings}
          >
            {savingSettings ? 'Shranjevanje...' : '💾 Shrani vse sistemske nastavitve'}
          </button>
        </>
      )}

      {/* ── UPORABNIKI TAB ─────────────────────────────────────────────────── */}
      {activeTab === 'users' && (
        <div className="glass-card">
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0 }}>Upravljanje Uporabnikov</h3>
            <button
              onClick={() => { setShowAddForm(f => !f); setAddError(''); }}
              className="btn-primary"
              style={{ padding: '0.5rem 1.1rem', fontSize: '0.9rem' }}
            >
              {showAddForm ? '✕ Zapri' : '＋ Dodaj uporabnika'}
            </button>
          </div>

          {/* Add user form */}
          {showAddForm && (
            <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#a5b4fc' }}>Nov uporabnik</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.8rem', marginBottom: '0.3rem' }}>E-naslov *</label>
                  <input type="email" value={addForm.email}
                    onChange={e => setAddForm({ ...addForm, email: e.target.value })}
                    placeholder="uporabnik@podjetje.si"
                    style={INPUT_STYLE}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.8rem', marginBottom: '0.3rem' }}>Geslo *</label>
                  <input type="password" value={addForm.password}
                    onChange={e => setAddForm({ ...addForm, password: e.target.value })}
                    placeholder="••••••••"
                    style={INPUT_STYLE}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.8rem', marginBottom: '0.3rem' }}>Ime podjetja</label>
                  <input type="text" value={addForm.company_name}
                    onChange={e => setAddForm({ ...addForm, company_name: e.target.value })}
                    placeholder="Podjetje d.o.o."
                    style={INPUT_STYLE}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.8rem', marginBottom: '0.3rem' }}>Začetni žetoni</label>
                  <input type="number" min="0" value={addForm.tokens_balance}
                    onChange={e => setAddForm({ ...addForm, tokens_balance: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </div>
              </div>
              {addError && (
                <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.9rem', borderRadius: '8px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', fontSize: '0.85rem' }}>
                  ⚠️ {addError}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                <button className="btn-primary" onClick={handleAddUser} disabled={addSaving || !addForm.email || !addForm.password}>
                  {addSaving ? 'Dodajam...' : '✓ Ustvari uporabnika'}
                </button>
                <button onClick={() => { setShowAddForm(false); setAddError(''); }}
                  style={{ background: 'transparent', border: '1px solid #374151', color: '#9ca3af', padding: '0.6rem 1rem', borderRadius: '8px', cursor: 'pointer' }}>
                  Prekliči
                </button>
              </div>
            </div>
          )}

          {/* Users table */}
          {loadingUsers ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem' }}>Nalagam uporabnike...</div>
          ) : users.length === 0 ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem' }}>Ni registriranih uporabnikov.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', color: '#e5e7eb' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #374151', color: '#9ca3af', fontSize: '0.85rem' }}>
                    <th style={{ padding: '0.75rem 0.5rem' }}>E-naslov</th>
                    <th style={{ padding: '0.75rem 0.5rem' }}>Podjetje</th>
                    <th style={{ padding: '0.75rem 0.5rem' }}>Žetoni</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} style={{ borderBottom: '1px solid #1f2937' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ padding: '0.85rem 0.5rem', fontWeight: 500 }}>{u.email}</td>
                      <td style={{ padding: '0.85rem 0.5rem', color: '#d1d5db' }}>{u.company_name || '—'}</td>
                      <td style={{ padding: '0.85rem 0.5rem' }}>
                        <span style={{ fontWeight: 600, color: u.tokens_balance === 0 ? '#f87171' : '#e0e0e0' }}>
                          {u.tokens_balance}
                        </span>
                      </td>
                      <td style={{ padding: '0.85rem 0.5rem', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                          <button className="btn-primary" onClick={() => handleAddTokens(u)}
                            style={{ padding: '0.35rem 0.7rem', fontSize: '0.78rem' }}>
                            +100
                          </button>
                          <button onClick={() => handleOpenEdit(u)}
                            style={{ background: 'transparent', border: '1px solid #374151', color: '#d1d5db', padding: '0.35rem 0.7rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem' }}>
                            Uredi
                          </button>
                          <button onClick={() => setDeleteUserId(u.id)}
                            style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', padding: '0.35rem 0.7rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem' }}>
                            Izbriši
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── EDIT MODAL ─────────────────────────────────────────────────────── */}
      {editUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="glass-card" style={{ width: '100%', maxWidth: '460px', margin: '1rem' }}>
            <h3 style={{ marginBottom: '1.5rem' }}>Uredi uporabnika</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.3rem' }}>E-naslov</label>
                <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} style={INPUT_STYLE} />
              </div>
              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                  Novo geslo <span style={{ color: '#6b7280' }}>(pusti prazno za ohranitev)</span>
                </label>
                <input type="password" value={editForm.password} onChange={e => setEditForm({ ...editForm, password: e.target.value })} placeholder="••••••••" style={INPUT_STYLE} />
              </div>
              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.3rem' }}>Ime podjetja</label>
                <input type="text" value={editForm.company_name} onChange={e => setEditForm({ ...editForm, company_name: e.target.value })} style={INPUT_STYLE} />
              </div>
              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.3rem' }}>Žetoni</label>
                <input type="number" min="0" value={editForm.tokens_balance} onChange={e => setEditForm({ ...editForm, tokens_balance: e.target.value })} style={INPUT_STYLE} />
              </div>
              {editError && (
                <div style={{ padding: '0.65rem 0.9rem', borderRadius: '8px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', fontSize: '0.85rem' }}>
                  ⚠️ {editError}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button className="btn-primary" onClick={handleSaveEdit} disabled={editSaving} style={{ flex: 1 }}>
                  {editSaving ? 'Shranjujem...' : '💾 Shrani'}
                </button>
                <button onClick={() => setEditUser(null)}
                  style={{ flex: 1, background: 'transparent', border: '1px solid #374151', color: '#d1d5db', borderRadius: '8px', cursor: 'pointer', padding: '0.65rem' }}>
                  Prekliči
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ───────────────────────────────────────────── */}
      {deleteUserId !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="glass-card" style={{ width: '100%', maxWidth: '380px', margin: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🗑️</div>
            <h3 style={{ marginBottom: '0.5rem' }}>Izbriši uporabnika?</h3>
            <p style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              {users.find(u => u.id === deleteUserId)?.email}<br />
              <span style={{ color: '#f87171' }}>To dejanje je nepovratno.</span>
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={handleDeleteUser} disabled={deleting}
                style={{ flex: 1, padding: '0.75rem', borderRadius: '8px', cursor: 'pointer', background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.4)', color: '#f87171', fontWeight: 600 }}>
                {deleting ? 'Brišem...' : 'Da, izbriši'}
              </button>
              <button onClick={() => setDeleteUserId(null)}
                style={{ flex: 1, padding: '0.75rem', borderRadius: '8px', cursor: 'pointer', background: 'transparent', border: '1px solid #374151', color: '#d1d5db' }}>
                Prekliči
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
